import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, ExternalLink } from 'lucide-react';
import axios from 'axios';
import '../styles/light-theme.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectPage = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${slug}`);
        setProject(response.data);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Project not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)'
      }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '20px',
        background: 'var(--bg-primary)'
      }}>
        <h2 style={{ color: 'var(--text-primary)' }}>Project not found</h2>
        <Link to="/" className="btn-primary">
          Back to Portfolio
        </Link>
      </div>
    );
  }

  const projectColor = project.color || '#0071e3';
  const projectMetrics = project.metrics || [];
  const projectMethods = project.methods || [];

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }} data-testid="project-page">
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '16px 32px',
        backdropFilter: 'blur(20px)',
        background: 'rgba(255, 255, 255, 0.8)',
        borderBottom: '1px solid var(--border-subtle)'
      }}>
        <Link 
          to="/" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'color 0.2s ease'
          }}
          data-testid="back-to-portfolio"
        >
          <ArrowLeft size={18} />
          <span>Back to Portfolio</span>
        </Link>
      </nav>

      {/* Hero Section */}
      <header style={{
        padding: '120px 32px 60px',
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        <div style={{ maxWidth: '700px' }}>
          <span style={{
            display: 'inline-block',
            padding: '6px 14px',
            background: projectColor,
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'white',
            marginBottom: '16px'
          }} data-testid="project-category">
            {project.category}
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '12px',
            letterSpacing: '-0.02em',
            lineHeight: 1.1
          }} data-testid="project-title">
            {project.title}
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'var(--text-secondary)',
            marginBottom: '24px'
          }} data-testid="project-subtitle">
            {project.subtitle}
          </p>
          
          {/* Meta Info */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
            {project.role && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
                <Users size={16} />
                <span>{project.role}</span>
              </div>
            )}
            {project.duration && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
                <Clock size={16} />
                <span>{project.duration}</span>
              </div>
            )}
          </div>

          {/* Methods Tags */}
          {projectMethods.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {projectMethods.map((method, idx) => (
                <span key={idx} style={{
                  padding: '5px 12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)'
                }}>
                  {method}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Hero Image */}
      <div style={{ padding: '0 32px 48px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)'
        }}>
          <img 
            src={project.image} 
            alt={project.title}
            style={{ width: '100%', height: 'auto', display: 'block' }}
            data-testid="project-hero-image"
          />
        </div>
      </div>

      {/* Metrics Section */}
      {projectMetrics.length > 0 && (
        <section style={{ padding: '0 32px 60px', maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(projectMetrics.length, 4)}, 1fr)`,
            gap: '16px'
          }} data-testid="project-metrics">
            {projectMetrics.map((metric, idx) => (
              <div key={idx} style={{
                background: 'var(--bg-card)',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 600, color: projectColor, marginBottom: '4px' }}>
                  {metric.value}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Content Sections */}
      <main style={{ padding: '0 32px 60px', maxWidth: '800px', margin: '0 auto' }}>
        {project.overview && (
          <ContentSection number="01" title="Overview" color={projectColor}>
            <p>{project.overview}</p>
          </ContentSection>
        )}

        {project.problem && (
          <ContentSection number="02" title="The Problem" color={projectColor}>
            <p>{project.problem}</p>
          </ContentSection>
        )}

        {project.process && (
          <ContentSection number="03" title="Process" color={projectColor}>
            <p>{project.process}</p>
          </ContentSection>
        )}

        {project.findings && (
          <ContentSection number="04" title="Key Findings" color={projectColor}>
            <p>{project.findings}</p>
          </ContentSection>
        )}

        {project.solutions && (
          <ContentSection number="05" title="Solutions" color={projectColor}>
            <p>{project.solutions}</p>
          </ContentSection>
        )}

        {project.outcomes && (
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '32px',
            marginTop: '40px'
          }}>
            <ContentSection number="06" title="Outcomes" color={projectColor} noMargin>
              <p>{project.outcomes}</p>
            </ContentSection>
          </div>
        )}
      </main>

      {/* Footer CTA */}
      <footer style={{
        padding: '60px 32px',
        background: 'var(--bg-secondary)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '10px' }}>
            Interested in working together?
          </h3>
          <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>
            Let's discuss how I can contribute to your team.
          </p>
          <a 
            href="mailto:adityapatil9997@gmail.com" 
            className="btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            data-testid="contact-cta"
          >
            Get In Touch
            <ExternalLink size={16} />
          </a>
        </div>
      </footer>
    </div>
  );
};

// Helper component for content sections
const ContentSection = ({ number, title, color, children, noMargin }) => (
  <section style={{ marginBottom: noMargin ? 0 : '48px' }}>
    <h2 style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '1.375rem',
      fontWeight: 600,
      color: 'var(--text-primary)',
      marginBottom: '16px'
    }}>
      <span style={{ fontSize: '0.875rem', fontWeight: 700, color: color }}>{number}</span>
      {title}
    </h2>
    <div style={{
      fontSize: '1rem',
      lineHeight: 1.7,
      color: 'var(--text-secondary)'
    }}>
      {children}
    </div>
  </section>
);

export default ProjectPage;
